import { useEffect, useRef, useState, type ReactNode } from "react";
import { isTitleUnchanged } from "@/domains/pm/issueTitle";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { GitBranch, History, ListChecks, Plus, Unlink, X } from "lucide-react";
import type { PMIssue, IssueStatus } from "@/domains/pm/types";
import { useIssuesStore, usePMStore, useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useStores } from "@/stores/useStores";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { PanelSectionLabel } from "./PanelSectionLabel";
import { DisciplinePicker } from "@/domains/pm/components/shared/DisciplinePicker";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { useResizablePanelWidth } from "@/hooks/useResizablePanelWidth";
import { useBottomRightExclusion } from "@/components/layout/useBottomRightExclusion";
import { IssueComments } from "./IssueComments";
import { IssueFieldGrid } from "./IssueFieldGrid";
import { isDateRangeInverted } from "@/domains/pm/utils/issueDates";
import { formatPanelDate, isDueDateOverdue, panelDateLocale } from "@/domains/pm/utils/panelDates";
import { IssueDocumentsSection } from "./IssueDocumentsSection";
import { IssueChecklistSection } from "./IssueChecklistSection";
import { IssueElementsSection } from "./IssueElementsSection";
import { DependencyField } from "./dependencies/DependencyField";
import { IssueFormDialog } from "./IssueFormDialog";
import { IssueDraftContent, type IssueDraftContentProps } from "./IssueDraftContent";
import { ParentIssuePickerDialog } from "./ParentIssuePickerDialog";
import { IssueActivityTimeline } from "./IssueActivityTimeline";
import { IssueOriginLine } from "./IssueOriginLine";
import { BriefOriginTag } from "@/domains/brief/components/BriefOriginTag";
import { useIssueMenuActions } from "./useIssueMenuActions";
import { ISSUE_TITLE_TEXTAREA_CLASS, useAutoFitTitle } from "./useAutoFitTitle";
import { useScheduleChangeApi } from "./schedule/scheduleChangeContext";
import { useScheduleStaging } from "./schedule/scheduleStagingContext";
import { useScheduleDragPreview } from "./schedule/scheduleDragPreview";
import { overlayIssue } from "@/domains/pm/hooks/useStagedSchedule";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { isOpenStatus } from "@/domains/pm/statuses/statusCatalogue";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import * as pmRepo from "@/stores/pm/repository";
import { useProjectResponsibilities } from "@/domains/contacts/useResponsibilities";
import { track } from "@/lib/posthog";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";

interface IssueDetailContentProps {
  issue: PMIssue;
  onClose: () => void;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** WHA-3331: navigate to a different issue (used by the parent-ref
   *  chip when the current issue is a subissue). Optional — kanban
   *  callers may not wire this. */
  onJumpToIssue?: (id: number) => void;
  /**
   * Read the issue without being able to change it.
   *
   * Set while a Gantt SNAPSHOT is open: the panel fetches the live row, so
   * beside a chart drawing the schedule as it was, an editable panel would
   * both contradict the chart and invite a write nobody meant to make. The
   * comment thread still reads; only its composer goes.
   */
  readOnly?: boolean;
}

/**
 * IssueDetailContent — the editing UI for a single issue. Used inline by
 * the list view's row expansion (WHA-3312) and wrapped by IssueDetailPanel
 * (below) for the kanban view's right-side panel. Layout chrome (width,
 * borders, background) is owned by callers so this component is
 * placement-agnostic.
 *
 * WHA-3432: restructured into dense, separator-divided bands — header,
 * compact metadata bar, documents, comments.
 */
export const IssueDetailContent = observer(function IssueDetailContent({
  issue,
  onClose,
  openViewer,
  onJumpToIssue,
  readOnly: snapshotReadOnly = false,
}: IssueDetailContentProps) {
  const { t, language } = useLanguage();
  // A member without MANAGE_PM in this project reads the issue as it is; any
  // edit they attempt is refused with a message naming who can grant it,
  // rather than saved optimistically and bounced by RLS. Comments stay open —
  // they need only VIEW_PM.
  const { canManagePMOnProject } = usePermissions();
  const denyPermission = usePermissionDenied();
  const cannotManage = !canManagePMOnProject(issue.building_project_id);
  const readOnly = snapshotReadOnly || cannotManage;
  const refuseEdit = () => void denyPermission("manageIssues", issue.building_project_id);
  // Run an edit, or refuse it when the member may not change this issue.
  const whenManaging = (edit: () => void) => () => {
    if (cannotManage && !snapshotReadOnly) {
      refuseEdit();
      return;
    }
    edit();
  };
  const schedule = useScheduleChangeApi();
  const staging = useScheduleStaging();
  // The dates as the chart beside this panel shows them: a staged but
  // unconfirmed change first, then a bar being dragged right now. The panel
  // and the chart must never disagree about where an issue sits.
  const scheduled = staging ? overlayIssue(issue, staging.staged) : issue;
  const dragging = useScheduleDragPreview(issue.id);
  const shownStart = dragging ? dragging.start : scheduled.start_date;
  const shownDue = dragging ? dragging.due : scheduled.due_date;
  const { catalogue: statusCatalogue } = useStatusCatalogue();
  const issuesStore = useIssuesStore();
  const pmStore = usePMStore();
  const projectMembers = useProjectMembersStore();
  const rootStore = useStores();
  const menuActions = useIssueMenuActions();
  // People responsible for this issue's disciplines (Contacts) head the assignee picker.
  const { responsibleFor } = useProjectResponsibilities(
    issue.building_id,
    issue.building_project_id
  );
  const [titleDraft, setTitleDraft] = useState(issue.title);
  const [startDraft, setStartDraft] = useState(shownStart ?? "");
  const [dueDraft, setDueDraft] = useState(shownDue ?? "");
  // Another issue opened in the same panel: its fields are reset in this
  // render rather than by the effects below, which run a commit later and
  // painted the previous issue's title over the new one for a frame or more.
  // An edit still running on the previous issue is unaffected — it carries
  // its own draft and is finished by the cleanup further down.
  const [draftsFor, setDraftsFor] = useState(issue.id);
  if (draftsFor !== issue.id) {
    setDraftsFor(issue.id);
    setTitleDraft(issue.title);
    setStartDraft(shownStart ?? "");
    setDueDraft(shownDue ?? "");
  }
  /**
   * A title edit in progress: which issue it belongs to, what the title was
   * when it began, and what has been typed. Kept in a ref, not state, so ending
   * the edit from a cleanup — the panel closing, another issue opening — still
   * reads the issue and the text it started with.
   */
  const titleEdit = useRef<{
    issueId: number;
    original: string;
    /** The stored title behind a localised display title (document parent issues). */
    originalRaw: string | undefined;
    draft: string;
  } | null>(null);
  // Title auto-fit: renders large for short titles and steps the font size
  // down as the title grows so it wraps across multiple lines before scrolling.
  const titleRef = useRef<HTMLTextAreaElement>(null);
  // WHA-3331 / WHA-3389
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [addSubissueOpen, setAddSubissueOpen] = useState(false);
  // WHA-3732 — issue history dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  // The Checklist section appears only once the issue has items or the user
  // asked for one. Per issue: switching issues drops an unused request.
  const [checklistRequested, setChecklistRequested] = useState(false);
  const [checklistFocusToken, setChecklistFocusToken] = useState(0);
  const openChecklist = () => {
    setChecklistRequested(true);
    setChecklistFocusToken((n) => n + 1);
  };
  // Radix hands focus back to the "+" trigger as the menu closes, which would
  // blur the just-opened composer and dismiss the empty checklist at once.
  const keepFocusOnClose = useRef(false);
  useEffect(() => {
    setChecklistRequested(false);
  }, [issue.id]);
  // "Add checklist" from the right-click menu reaches the panel through the
  // store: the panel may not even be open when the menu item is chosen.
  const checklistRequestIssueId = issuesStore.checklistRequestIssueId;
  useEffect(() => {
    if (readOnly || checklistRequestIssueId !== issue.id) return;
    setChecklistRequested(true);
    setChecklistFocusToken((n) => n + 1);
    issuesStore.clearChecklistRequest();
  }, [checklistRequestIssueId, issue.id, issuesStore, readOnly]);
  const isSubissue = issue.parent_issue_id != null;
  const parentIssue = isSubissue
    ? (issuesStore.issues.find((i) => i.id === issue.parent_issue_id) ?? null)
    : null;
  // WHA-3455: an issue that already has subissues can't itself become a
  // subissue — hide "Mark as subissue" in the overflow menu for it.
  const hasSubissues = issuesStore.issues.some((i) => i.parent_issue_id === issue.id);

  // The issue row behind this panel updates live off `pm_issues`, but the
  // thread inside it was read once on open — so a colleague's comment simply
  // never appeared until you clicked away and back.
  //
  // Bound to `reloadSelectedComments`, never `selectIssue`: that one also marks
  // the current user's mentions read, which would fire because somebody else
  // posted.
  useEffect(() => {
    const thread = pmRepo.subscribeToComments(issue.id, () => {
      void issuesStore.reloadSelectedComments(issue.id);
    });
    // The handle's own teardown, never removeChannel: reopening the same issue
    // (protocol dialog → panel) would otherwise let the new channel claim the
    // topic while the old one is still being released.
    return () => thread.unsubscribe();
  }, [issue.id, issuesStore]);

  // Follow the issue as it changes elsewhere — the chart, the list, a
  // colleague, the chatbot. The title is left alone while it is being typed:
  // a refresh landing mid-word would throw the user's text away.
  useEffect(() => {
    if (titleEdit.current === null) setTitleDraft(issue.title);
  }, [issue.id, issue.title]);
  useEffect(() => {
    setStartDraft(shownStart ?? "");
    setDueDraft(shownDue ?? "");
  }, [issue.id, shownStart, shownDue]);

  // Auto-fit the title — shared with the New issue draft, which must read the same.
  useAutoFitTitle(titleRef, titleDraft);

  // Disciplines + project users feed the inline editors. WHA-3507: the
  // assignee picker is scoped to users with access to this issue's project.
  useEffect(() => {
    pmStore.loadDisciplines();
  }, [pmStore]);

  useEffect(() => {
    if (issue.building_project_id) {
      projectMembers.fetch(issue.building_project_id);
    }
  }, [issue.building_project_id, projectMembers]);

  const comments = issuesStore.selectedIssueComments;
  const linkedDocuments = issue.linked_documents ?? [];
  const linkedElements = issue.linked_elements ?? [];

  // Optimistic per-field save. On failure we reload the issue list to revert
  // any displayed value to the server-authoritative one.
  const saveField = async (patch: Partial<PMIssue>) => {
    if (readOnly) {
      if (!snapshotReadOnly) refuseEdit();
      return;
    }
    // Read before the optimistic patch rewrites it.
    const fromStatus = issue.status;
    issuesStore.updateIssueOptimistic(issue.id, patch);
    try {
      await issuesStore.updateIssue(issue.id, patch);
      if (patch.status && patch.status !== fromStatus) {
        track("issue_status_changed", {
          from_status: fromStatus,
          to_status: patch.status,
          bulk: false,
        });
      }
    } catch (err) {
      await issuesStore.loadIssues(issue.building_id);
      toast.error(friendlyIssueError(err, t));
    }
  };

  /**
   * The title is mirrored into the list and the chart as it is typed, so every
   * surface reads the same name while it changes. It is held over reloads until
   * editing ends, then saved — or put back, if it ended empty, unchanged or
   * cancelled.
   */
  const beginTitleEdit = () => {
    titleEdit.current ??= {
      issueId: issue.id,
      original: issue.title,
      originalRaw: issue.title_raw,
      draft: issue.title,
    };
    return titleEdit.current;
  };

  const handleTitleChange = (value: string) => {
    setTitleDraft(value);
    if (readOnly) return;
    const edit = beginTitleEdit();
    edit.draft = value;
    issuesStore.holdEdit(edit.issueId, { title: value });
    issuesStore.updateIssueOptimistic(edit.issueId, { title: value });
  };

  const finishTitleEdit = (keep: boolean) => {
    const edit = titleEdit.current;
    titleEdit.current = null;
    if (!edit) return;
    const stillOpen = edit.issueId === issueIdRef.current;
    issuesStore.dropEdit(edit.issueId, ["title"]);
    const next = edit.draft.trim();
    // Unchanged includes the stored title behind a localised display title —
    // writing that back would count as a rename in the database and retire a
    // document parent issue's automatic title.
    const unchanged = isTitleUnchanged({ title: edit.original, title_raw: edit.originalRaw }, next);
    if (!keep || !next || unchanged) {
      issuesStore.updateIssueOptimistic(edit.issueId, { title: edit.original });
      if (stillOpen) setTitleDraft(edit.original);
      return;
    }
    if (stillOpen) setTitleDraft(next);
    issuesStore.updateIssueOptimistic(edit.issueId, { title: next });
    issuesStore.updateIssue(edit.issueId, { title: next }).catch((err: unknown) => {
      void issuesStore.loadIssues(issue.building_id);
      toast.error(friendlyIssueError(err, t));
    });
  };

  // Closing the panel or opening another issue mid-edit ends the edit the way
  // leaving the field does. The ref is refreshed every render, so the cleanup
  // calls the current function; the edit itself carries its own issue.
  const issueIdRef = useRef(issue.id);
  issueIdRef.current = issue.id;
  const finishTitleEditRef = useRef(finishTitleEdit);
  finishTitleEditRef.current = finishTitleEdit;
  useEffect(() => () => finishTitleEditRef.current(true), [issue.id]);

  // Dates go through the schedule path, not saveField: this issue may drive
  // others, and a panel edit that quietly left its successors behind would be
  // the same change behaving differently from a Gantt drag.
  const saveDate = (patch: { start_date?: string | null; due_date?: string | null }) => {
    if (schedule) {
      // With the staged dates: the request completes the pair from the issue
      // it is given, and the store's copy would quietly undo a staged change
      // to the other end.
      void schedule.request(scheduled, patch);
      return;
    }
    void saveField(patch);
  };

  const handleStartDateChange = (v: string) => {
    setStartDraft(v);
    saveDate({ start_date: v || null });
  };

  const handleDueDateChange = (v: string) => {
    setDueDraft(v);
    saveDate({ due_date: v || null });
  };

  const handleStatusChange = (next: IssueStatus) => {
    if (next === issue.status) return;
    saveField({ status: next });
  };

  // WHA-3448: route assignee/discipline edits through the shared menu
  // actions so they apply optimistically — instantly in both the list row
  // and this expanded view — exactly like the right-click context menu.
  const handleAssigneesChange = (userIds: number[]) => {
    const prev = (issue.assignees ?? []).map((a) => a.id);
    menuActions.applyAssignees([issue], issue.building_id, userIds, prev);
  };

  const handleDisciplinesChange = (disciplineIds: number[]) => {
    const prev = (issue.disciplines ?? []).map((d) => d.id);
    menuActions.applyDisciplines([issue], issue.building_id, disciplineIds, prev);
  };

  const handleRemoveDocument = async (documentId: number) => {
    try {
      await pmRepo.removeIssueDocument(issue.id, documentId);
      await issuesStore.selectIssue(issue.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove document");
    }
  };

  const handleAttachmentsChanged = () => {
    issuesStore.selectIssue(issue.id);
  };

  /** Resolves false when nothing was posted; the composer then restores the draft. */
  const handleAddComment = (
    body: string,
    mentionedUserIds: number[],
    documentIds: number[] = []
  ): Promise<boolean> => {
    const authorId = rootStore.userStore.userProfile?.id;
    if (!authorId) return Promise.resolve(false);
    return issuesStore.addComment(issue.id, authorId, body, mentionedUserIds, documentIds).then(
      () => true,
      (err: unknown) => {
        toast.error(err instanceof Error ? err.message : t.pm.issues.detail.commentFailed);
        return false;
      }
    );
  };

  // WHA-3453: edit one of the user's own comments.
  const handleEditComment = (commentId: number, body: string, mentionedUserIds: number[]) => {
    const userId = rootStore.userStore.userProfile?.id;
    if (!userId) return;
    issuesStore
      .editComment(commentId, issue.id, body, userId, mentionedUserIds)
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : "Failed to edit comment");
      });
  };

  const assigneeIds = issue.assignees?.map((a) => a.id) ?? [];
  const selectedDisciplineIds = issue.disciplines?.map((d) => d.id) ?? [];
  // WHA-3507: assignee dropdown scoped to project members. Current assignees
  // are unioned in: the members list loads asynchronously (and can omit someone
  // who was assigned before losing project access), and the field renders only
  // the users it is given — without this the panel would show fewer assignees
  // than the list row does.
  const projectMemberUsers = projectMembers.get(issue.building_project_id);
  const assigneeUsers = issue.assignees ?? [];
  const memberIds = new Set(projectMemberUsers.map((u) => u.id));
  const projectUsers = [
    ...projectMemberUsers,
    ...assigneeUsers.filter((a) => !memberIds.has(a.id)),
  ];
  // Only current members are suggested; a former assignee stays pickable
  // above, but is never put forward as responsible.
  const suggestedAssigneeIds = responsibleFor(selectedDisciplineIds).filter((uid) =>
    memberIds.has(uid)
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header row — the issue number and the panel actions only, on the same
          horizontal padding as the body so "#41" aligns with the section
          labels below and the close icon mirrors it (WHA-3810 round 2). No
          hairline under it: the panel keeps exactly one divider, the one below
          the title (see the body below). */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 px-5 py-4">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[11px] font-medium text-muted-foreground">
            #{issue.issue_number}
          </span>
          <IssueOriginLine issueId={issue.id} buildingId={issue.building_id} />
          {/* Created from a brief appendix: links back to it. */}
          <BriefOriginTag issueId={issue.id} />
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {/* Everything that adds to the issue — a checklist, subissues —
              behind one "+", the left-most of the header icons. Shown to a
              member without MANAGE_PM too: picking an item refuses with a
              message, like every other edit here. Only a snapshot hides it. */}
          {!snapshotReadOnly && (
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      aria-label={t.pm.issues.checklist.addToIssue}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>{t.pm.issues.checklist.addToIssue}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                align="end"
                className="w-48"
                onCloseAutoFocus={(e) => {
                  if (!keepFocusOnClose.current) return;
                  keepFocusOnClose.current = false;
                  e.preventDefault();
                }}
              >
                <DropdownMenuItem
                  onSelect={whenManaging(() => {
                    keepFocusOnClose.current = true;
                    openChecklist();
                  })}
                >
                  <ListChecks className="mr-2 h-3.5 w-3.5" />
                  {t.pm.issues.checklist.addChecklist}
                </DropdownMenuItem>
                {isSubissue ? (
                  <DropdownMenuItem
                    onSelect={whenManaging(() => void saveField({ parent_issue_id: null }))}
                  >
                    <Unlink className="mr-2 h-3.5 w-3.5" />
                    {t.pm.issues.detail.removeFromParent}
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onSelect={whenManaging(() => setAddSubissueOpen(true))}>
                      <Plus className="mr-2 h-3.5 w-3.5" />
                      {t.pm.issues.detail.addSubissue}
                    </DropdownMenuItem>
                    {!hasSubissues && (
                      <DropdownMenuItem onSelect={whenManaging(() => setParentPickerOpen(true))}>
                        <GitBranch className="mr-2 h-3.5 w-3.5" />
                        {t.pm.issues.detail.markAsSubissue}
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* WHA-3732: open the issue change history */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setHistoryOpen(true)}
                aria-label={t.pm.issues.detail.history}
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
              >
                <History className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.pm.issues.detail.history}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="Close detail panel"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.common.close}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* The whole body — fields through comments — scrolls as one block, so
          the comment input simply follows the last content section instead of
          being pinned to the panel floor with dead space above it (WHA-3810). */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* One content container owns the body's horizontal padding, and a
            uniform 10px band padding gives the ~20px rhythm between sections.
            Sections are separated by that rhythm alone — the title band's
            `border-b` is the panel's only hairline, marking where the issue's
            identity ends and its editable fields begin. */}
        <div className="px-5 pb-4">
          <div className="border-b py-2.5">
            {/* WHA-3331: parent reference for subissues */}
            {isSubissue && parentIssue && (
              <div className="mb-1 flex max-w-full items-center gap-1">
                <button
                  type="button"
                  onClick={() => onJumpToIssue?.(parentIssue.id)}
                  className="inline-flex min-w-0 items-center truncate rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                  title={t.pm.issues.detail.subissueOf
                    .replace("{n}", String(parentIssue.issue_number))
                    .replace("{title}", parentIssue.title)}
                >
                  ↑ #{parentIssue.issue_number} {parentIssue.title}
                </button>
                {/* The same write as "Remove from parent" in the menus, put where
                    the relationship is shown — buried there, it went unfound. */}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => saveField({ parent_issue_id: null })}
                    className="inline-flex shrink-0 items-center rounded border border-border px-1.5 py-0.5 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                    title={t.pm.issues.detail.makeTopLevel}
                    aria-label={t.pm.issues.detail.makeTopLevel}
                  >
                    <Unlink className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
            <textarea
              ref={titleRef}
              rows={1}
              readOnly={readOnly}
              value={titleDraft}
              onFocus={() => {
                if (!readOnly) beginTitleEdit();
              }}
              onClick={cannotManage && !snapshotReadOnly ? refuseEdit : undefined}
              onChange={(e) => handleTitleChange(e.target.value)}
              onBlur={() => finishTitleEdit(true)}
              onKeyDown={(e) => {
                // Title is a single value — Enter saves instead of inserting a newline.
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLTextAreaElement).blur();
                }
                if (e.key === "Escape") {
                  // Put the title back before the blur, which then has nothing to save.
                  finishTitleEdit(false);
                  (e.target as HTMLTextAreaElement).blur();
                }
              }}
              className={ISSUE_TITLE_TEXTAREA_CLASS}
            />
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {t.pm.issues.detail.created}{" "}
              {formatPanelDate(issue.created_at, panelDateLocale(language))}
            </p>
          </div>

          <IssueFieldGrid
            orgUsers={projectUsers}
            assigneeIds={assigneeIds}
            onAssigneesChange={handleAssigneesChange}
            suggestedAssigneeIds={suggestedAssigneeIds}
            startDate={startDraft}
            onStartDateChange={handleStartDateChange}
            dueDate={dueDraft}
            onDueDateChange={handleDueDateChange}
            isOverdue={isDueDateOverdue(
              dueDraft,
              !isOpenStatus(statusCatalogue, issue.building_project_id, issue.status)
            )}
            // Read from the drafts, not the issue: the warning must appear on
            // the edit that creates the inversion, before the save round-trips.
            isDateRangeInverted={isDateRangeInverted({
              start_date: startDraft || null,
              due_date: dueDraft || null,
            })}
            status={issue.status}
            buildingProjectId={issue.building_project_id}
            onStatusChange={handleStatusChange}
            readOnly={readOnly}
            // Dependencies sit with the other properties of the issue rather
            // than beside its tags: what an issue waits on is scheduling, and
            // it belongs next to the dates it constrains.
            dependencies={<DependencyField issue={issue} readOnly={readOnly} />}
          />

          {/* Tags (disciplines) — quiet outline pills with a "+" to add one. */}
          <div className="py-2.5">
            <PanelSectionLabel className="mb-1.5">{t.pm.issues.detail.tags}</PanelSectionLabel>
            <DisciplinePicker
              all={pmStore.disciplines}
              selectedIds={selectedDisciplineIds}
              onChange={handleDisciplinesChange}
              emptyLabel={t.pm.issues.detail.addTag}
              badgeSize="md"
              badgeVariant="outline"
              addAffordance={!readOnly}
              disabled={readOnly}
              triggerClassName="-ml-1 min-h-8 max-w-full gap-1.5 px-1 py-0.5"
              fitLabels
              badgeRowClassName="gap-1.5"
            />
          </div>

          {/* Checklist — ordered items to tick off. */}
          <IssueChecklistSection
            issueId={issue.id}
            readOnly={readOnly}
            requested={checklistRequested}
            focusToken={checklistFocusToken}
            onDismiss={() => setChecklistRequested(false)}
          />

          {/* Linked documents */}
          <IssueDocumentsSection
            issueId={issue.id}
            buildingId={issue.building_id}
            buildingProjectId={issue.building_project_id}
            linkedDocuments={linkedDocuments}
            openViewer={openViewer}
            onRemoveDocument={handleRemoveDocument}
            onAttachmentsChanged={handleAttachmentsChanged}
            readOnly={readOnly}
          />

          {/* Linked BIM elements (WHA-3521) — only shown when the issue actually
              references IFC elements; absent entirely otherwise. */}
          {linkedElements.length > 0 && (
            <div className="py-2.5">
              <PanelSectionLabel className="mb-1.5">
                {t.pm.issues.detail.elements} · {linkedElements.length}
              </PanelSectionLabel>
              <IssueElementsSection
                issueId={issue.id}
                buildingId={issue.building_id}
                projectId={issue.building_project_id ?? null}
                elements={linkedElements}
                onChange={handleAttachmentsChanged}
              />
            </div>
          )}

          {/* Comment thread then its input, under a "COMMENTS · N" label. */}
          <IssueComments
            comments={comments}
            issueId={issue.id}
            buildingId={issue.building_id}
            buildingProjectId={issue.building_project_id}
            openViewer={openViewer}
            onAddComment={handleAddComment}
            onEditComment={handleEditComment}
            currentUserId={rootStore.userStore.userProfile?.id}
            currentUser={rootStore.userStore.userProfile}
            readOnly={snapshotReadOnly}
            loading={issuesStore.isLoadingSelectedComments}
          />
        </div>
      </div>

      {/* WHA-3389 — pick a parent for an existing top-level issue */}
      <ParentIssuePickerDialog
        open={parentPickerOpen}
        onOpenChange={setParentPickerOpen}
        buildingId={issue.building_id}
        currentIssueId={issue.id}
        onPicked={(parentId) => saveField({ parent_issue_id: parentId })}
      />

      {/* WHA-3331 — create a new subissue with this issue as the parent */}
      <IssueFormDialog
        open={addSubissueOpen}
        onOpenChange={setAddSubissueOpen}
        buildingId={issue.building_id}
        defaultProjectId={String(issue.building_project_id)}
        parentIssueId={issue.id}
      />

      {/* WHA-3732 — issue change history */}
      <IssueActivityTimeline issueId={issue.id} open={historyOpen} onOpenChange={setHistoryOpen} />
    </div>
  );
});

// Bumped v2 → v3 with the "only a deliberate resize is persisted" rule: the
// old hook wrote the loaded/clamped width 200ms after every mount, so anyone
// who had opened this panel once already had a viewport-sized value under v2
// that `loadStoredWidth` cannot tell from a real preference — it would pin a
// laptop's 35 % onto a monitor forever. A new key discards those auto-writes.
const ISSUE_PANEL_STORAGE_KEY = "issue-detail-panel:v3";
// Default the panel to 35 % of the viewport so it opens roomy on wide
// monitors (e.g. reaching the kanban's last column) while staying sensible on
// laptops. The hook still clamps to [360px, 65 % of viewport] and persists any
// manual resize under the key above.
const DEFAULT_WIDTH_RATIO = 0.35;

/**
 * The right-anchored overlay both panels share: the persisted, resizable width,
 * the bottom-right exclusion the chat pill reads, Escape to close, and the
 * `data-issue-panel` marker that outside-click handlers look for.
 *
 * Escape is ignored while a text field is focused (title, comment box), so
 * Escape there only cancels the edit, and when something nearer already
 * handled it — a Radix menu, popover or dialog marks its own Escape
 * `defaultPrevented` while closing itself, and the panel must not close with it.
 */
function IssuePanelChrome({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { width, settledWidth, dragging, hover, handleProps } = useResizablePanelWidth(
    ISSUE_PANEL_STORAGE_KEY,
    Math.round((typeof window !== "undefined" ? window.innerWidth : 1440) * DEFAULT_WIDTH_RATIO)
  );
  useBottomRightExclusion("issue-detail-panel", settledWidth);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <aside
      aria-label={label}
      data-issue-panel
      className="border-l bg-background"
      style={{
        position: "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        width,
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "-18px 0 40px -24px rgba(40,30,15,0.22)",
      }}
    >
      {/* Left-edge resize handle. A subtle accent line appears on hover/drag;
          the hit area is wider than the visible line for easy grabbing. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        {...handleProps}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          zIndex: 2,
          touchAction: "none",
          background: dragging || hover ? "hsl(var(--primary) / 0.4)" : "transparent",
          transition: "background 120ms",
        }}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </aside>
  );
}

/**
 * IssueDetailPanel — fixed, right-anchored overlay chrome around
 * IssueDetailContent (WHA-3644). Used by both the kanban and list views: it
 * floats ON TOP of the board/list rather than reflowing them, and the
 * left-edge handle resizes it, mirroring the building data sidebar. The
 * persisted width lives under `issue-detail-panel:v3` in localStorage.
 */
export function IssueDetailPanel(props: IssueDetailContentProps) {
  return (
    <IssuePanelChrome label="Issue details" onClose={props.onClose}>
      <IssueDetailContent {...props} />
    </IssuePanelChrome>
  );
}

export type IssueDraftPanelProps = IssueDraftContentProps & { onClose: () => void };

/**
 * The /issues page's New issue: the same panel, same width, holding a draft
 * (`IssueDraftContent`) instead of an issue. Nothing in it writes — the page
 * saves or discards the draft when it is left (`draftLeaveAction`).
 */
export function IssueDraftPanel(props: IssueDraftPanelProps) {
  const { t } = useLanguage();
  return (
    <IssuePanelChrome label={t.pm.issues.draft.newIssue} onClose={props.onClose}>
      <IssueDraftContent {...props} />
    </IssuePanelChrome>
  );
}
